class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement || !parsedState.sections?.[section.id]) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

class DeeowCartRecommendations extends HTMLElement {
  connectedCallback() {
    if (this.dataset.loaded === 'true') return;
    this.dataset.loaded = 'true';
    this.productIds = this.readIds('productIds');
    this.variantIds = new Set(this.readIds('variantIds'));
    this.productsContainer = this.querySelector('.deeow-cart-upsell__products');
    this.errorElement = this.querySelector('.deeow-cart-upsell__error');
    this.addEventListener('click', this.handleClick.bind(this));
    this.loadRecommendations();
  }

  readIds(key) {
    return (this.dataset[key] || '')
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean);
  }

  async fetchRecommendations(productId, intent) {
    const root = window.Shopify?.routes?.root || '/';
    const url = `${root}recommendations/products.json?product_id=${encodeURIComponent(productId)}&limit=4&intent=${intent}`;
    const response = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!response.ok) return [];
    const data = await response.json();
    return Array.isArray(data.products) ? data.products : [];
  }

  async loadRecommendations() {
    if (!this.productIds.length || !this.productsContainer) return;

    try {
      const selectedIds = this.productIds.slice(0, 4);
      const complementaryGroups = await Promise.all(
        selectedIds.map((productId) => this.fetchRecommendations(productId, 'complementary')),
      );
      const relatedGroups = await Promise.all(
        selectedIds.map((productId) => this.fetchRecommendations(productId, 'related')),
      );
      const products = [...complementaryGroups.flat(), ...relatedGroups.flat()];

      const seenProducts = new Set();
      const cartProducts = new Set(this.productIds);
      const recommendations = [];

      for (const product of products) {
        const productId = String(product.id);
        const variant = (product.variants || []).find(
          (candidate) => candidate.available && !this.variantIds.has(String(candidate.id)),
        );

        if (!variant || cartProducts.has(productId) || seenProducts.has(productId)) continue;
        seenProducts.add(productId);
        recommendations.push({ product, variant });
        if (recommendations.length === 2) break;
      }

      if (!recommendations.length) return;
      recommendations.forEach(({ product, variant }) => this.renderProduct(product, variant));
      this.hidden = false;
    } catch (error) {
      this.hidden = true;
    }
  }

  renderProduct(product, variant) {
    const item = document.createElement('div');
    item.className = 'deeow-cart-upsell__item';
    item.setAttribute('role', 'listitem');

    const imageLink = document.createElement('a');
    imageLink.className = 'deeow-cart-upsell__image-link';
    imageLink.href = product.url;
    imageLink.setAttribute('aria-label', product.title);

    const imageSource = typeof product.featured_image === 'string'
      ? product.featured_image
      : product.featured_image?.url || product.images?.[0];
    if (imageSource) {
      const image = document.createElement('img');
      image.className = 'deeow-cart-upsell__image';
      image.src = imageSource;
      image.alt = product.title;
      image.loading = 'lazy';
      image.width = 124;
      image.height = 124;
      imageLink.appendChild(image);
    }

    const details = document.createElement('div');
    const title = document.createElement('a');
    title.className = 'deeow-cart-upsell__title';
    title.href = product.url;
    title.textContent = product.title;

    const price = document.createElement('p');
    price.className = 'deeow-cart-upsell__price';
    price.textContent = this.formatMoney(Number(variant.price));
    details.append(title, price);

    const button = document.createElement('button');
    button.className = 'deeow-cart-upsell__add';
    button.type = 'button';
    button.dataset.variantId = variant.id;
    button.textContent = 'Add +';
    button.setAttribute('aria-label', `Add ${product.title} to cart`);

    item.append(imageLink, details, button);
    this.productsContainer.appendChild(item);
  }

  formatMoney(priceInCents) {
    const currency = this.dataset.currency || window.Shopify?.currency?.active || 'USD';
    const locale = document.documentElement.lang || 'en-US';
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
    }).format(priceInCents / 100);
  }

  async handleClick(event) {
    const button = event.target.closest('.deeow-cart-upsell__add');
    if (!button || button.disabled) return;

    button.disabled = true;
    button.textContent = 'Adding…';
    this.showError('');

    try {
      const cartDrawer = document.querySelector('cart-drawer');
      const sections = cartDrawer.getSectionsToRender().map((section) => section.id);
      const addUrl = window.routes?.cart_add_url || `${window.Shopify?.routes?.root || '/'}cart/add.js`;
      const response = await fetch(addUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [{ id: Number(button.dataset.variantId), quantity: 1 }],
          sections,
          sections_url: window.location.pathname,
        }),
      });
      const data = await response.json();

      if (!response.ok || data.status) throw new Error(data.description || 'Unable to add this item.');
      cartDrawer.renderContents(data);
    } catch (error) {
      button.disabled = false;
      button.textContent = 'Add +';
      this.showError(error.message || 'Unable to add this item. Please try again.');
    }
  }

  showError(message) {
    if (!this.errorElement) return;
    this.errorElement.textContent = message;
    this.errorElement.hidden = !message;
  }
}

if (!customElements.get('deeow-cart-recommendations')) {
  customElements.define('deeow-cart-recommendations', DeeowCartRecommendations);
}
